import React from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { Grid } from '@atlaskit/primitives/grid';
import { xcss } from '@atlaskit/primitives/xcss';

import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { EndOfPageCard } from '@confluence/page-card/entry-points/EndOfPageCard.ts';
import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';

import { ThirdPartyPromptSlot } from './ThirdPartyPromptSlot.tsx';

export type EndOfPageRecGridProps = {
	pageCardData: GlobalPageCard[];
	pageRecStrategy?: string[][];
	entityId: string;
	isCompact?: boolean;
};

const TwoCardWrapperWidth = xcss({
	maxWidth: '480px',
});

const ThreeCardWrapperWidth = xcss({
	maxWidth: '320px',
});

const BigCardHeight = xcss({
	height: '128px',
});

const getColumnsBasedOnNumberOfCards = (totalCards: number) => {
	const fixedTwoColumns = '1fr 1fr';
	const autoFitColumns = 'repeat(auto-fit, minmax(320px, 1fr))';
	switch (totalCards) {
		case 1:
		case 2:
			return fixedTwoColumns;
		default:
			return autoFitColumns;
	}
};

const getCardStyles = (totalCards: number) => {
	let stylesArray;
	switch (totalCards) {
		case 1:
		case 2:
		case 4:
			stylesArray = [TwoCardWrapperWidth];
			break;
		default:
			stylesArray = [ThreeCardWrapperWidth];
			break;
	}
	stylesArray.push(BigCardHeight);
	return stylesArray;
};

export const EndOfPageRecGrid = ({
	pageCardData,
	pageRecStrategy,
	entityId,
	isCompact,
}: EndOfPageRecGridProps): React.JSX.Element => {
	return (
		<Grid
			templateColumns={getColumnsBasedOnNumberOfCards(pageCardData.length)}
			gap="space.200"
			role="list"
		>
			{pageCardData.map((node, i) => {
				const card = (
					<EndOfPageCard
						data={node}
						ranking={i}
						analyticsData={{
							source: 'endOfPageRecommendations',
							attributes: {
								strategy: pageRecStrategy ? pageRecStrategy[i % pageRecStrategy.length] : null,
								mainContentId: entityId,
							},
						}}
						isCompactView={isCompact}
						showCardsAsListitem={false}
					/>
				);

				const isLowestRanked = i === pageCardData.length - 1;

				return (
					<Box key={node.id} xcss={() => getCardStyles(pageCardData.length)} role="listitem">
						{isLowestRanked &&
						// True for control and treatment. Exposure still fires in ThirdPartyPromptSlot
						// via isEnabled, at the moment the prompt would show.
						// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
						UNSAFE_expValEqualsNoExposure(
							'confluence_3p_in_related_content',
							'shouldEvaluate',
							true,
						) ? (
							<ThirdPartyPromptSlot entityId={entityId}>{card}</ThirdPartyPromptSlot>
						) : (
							card
						)}
					</Box>
				);
			})}
		</Grid>
	);
};
